import React, { useCallback, useMemo, useRef, useState } from 'react';

export interface SpriteMeta {
  url: string;
  tilesWide: number;
  tilesHigh: number;
  tileWidth: number;
  tileHeight: number;
  durationSec: number;
}

export interface Chapter {
  /** Start time in seconds. Chapters are inferred from neighbour starts. */
  time: number;
  title: string;
}

export interface SeekBarProps {
  currentTime: number;
  duration: number;
  buffered: number;
  thickness?: number;
  sprite?: SpriteMeta | null;
  chapters?: Chapter[];
  onSeek: (time: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

interface Segment {
  start: number;
  end: number;
  title?: string;
}

/**
 * Computes the chapter segments to render. When no chapters are supplied,
 * returns a single full-width segment so the bar still gets the hover-grow
 * + accent-fill treatment.
 */
function computeSegments(chapters: Chapter[] | undefined, duration: number): Segment[] {
  if (!duration || duration <= 0) return [{ start: 0, end: 1 }];
  if (!chapters || chapters.length === 0) return [{ start: 0, end: 1 }];
  const sorted = [...chapters].sort((a, b) => a.time - b.time);
  const segs: Segment[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const start = Math.max(0, sorted[i].time) / duration;
    const end = (sorted[i + 1]?.time ?? duration) / duration;
    if (end <= start) continue;
    segs.push({ start, end, title: sorted[i].title });
  }
  return segs.length > 0 ? segs : [{ start: 0, end: 1 }];
}

const SeekBar: React.FC<SeekBarProps> = ({
  currentTime,
  duration,
  buffered,
  thickness = 4,
  sprite,
  chapters,
  onSeek,
  onScrubStart,
  onScrubEnd,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [hoverRatio, setHoverRatio] = useState<number | null>(null);

  const progress = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;
  const bufRatio = duration > 0 ? Math.min(1, Math.max(0, buffered / duration)) : 0;

  const segments = useMemo(() => computeSegments(chapters, duration), [chapters, duration]);

  const ratioAt = useCallback((clientX: number, rect: DOMRect) => {
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    return rect.width > 0 ? x / rect.width : 0;
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!trackRef.current || duration <= 0) return;
    e.preventDefault();
    trackRef.current.setPointerCapture(e.pointerId);
    setDragging(true);
    onScrubStart?.();
    const rect = trackRef.current.getBoundingClientRect();
    const r = ratioAt(e.clientX, rect);
    setHoverRatio(r);
    onSeek(r * duration);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const r = ratioAt(e.clientX, rect);
    setHoverRatio(r);
    if (dragging) {
      onSeek(r * duration);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (trackRef.current) trackRef.current.releasePointerCapture(e.pointerId);
    if (dragging) onScrubEnd?.();
    setDragging(false);
  };

  const handlePointerLeave = () => {
    if (!dragging) setHoverRatio(null);
  };

  const showTooltip = hoverRatio !== null && duration > 0;
  const hoverTime = (hoverRatio ?? 0) * duration;

  // Find the chapter title at the hovered position (segments are already
  // sorted + normalized so a linear scan is fine).
  const hoverChapter = useMemo(() => {
    if (hoverRatio === null) return undefined;
    const seg = segments.find((s) => hoverRatio >= s.start && hoverRatio < s.end);
    return seg?.title;
  }, [hoverRatio, segments]);

  // Sprite-tile background math: pick the tile whose timestamp is closest to
  // the hovered position. Falls back to the CSS gradient placeholder when no
  // sprite is available.
  let tileStyle: React.CSSProperties | null = null;
  if (sprite) {
    const totalTiles = Math.max(1, sprite.tilesWide * sprite.tilesHigh);
    const ratio = sprite.durationSec > 0 ? hoverTime / sprite.durationSec : 0;
    const idx = Math.max(0, Math.min(totalTiles - 1, Math.floor(ratio * totalTiles)));
    const col = idx % sprite.tilesWide;
    const row = Math.floor(idx / sprite.tilesWide);
    tileStyle = {
      width: sprite.tileWidth,
      height: sprite.tileHeight,
      backgroundImage: `url(${sprite.url})`,
      backgroundPosition: `-${col * sprite.tileWidth}px -${row * sprite.tileHeight}px`,
      backgroundSize: `${sprite.tilesWide * sprite.tileWidth}px ${sprite.tilesHigh * sprite.tileHeight}px`,
      backgroundRepeat: 'no-repeat',
    };
  }

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.max(0, Math.round(duration))}
      aria-valuenow={Math.round(currentTime)}
      aria-valuetext={hoverChapter ? `${formatTime(currentTime)}, ${hoverChapter}` : formatTime(currentTime)}
      tabIndex={-1}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      className="oden-scrub"
      style={{ ['--th' as string]: thickness + 'px' } as React.CSSProperties}
    >
      {showTooltip && (
        <div className="oden-scrub-tip" style={{ left: `${(hoverRatio ?? 0) * 100}%` }}>
          <div className="oden-scrub-thumb">
            {tileStyle ? <div className="oden-scrub-thumb-img" style={tileStyle} /> : <div className="oden-scrub-thumb-img" />}
            <div className="oden-scrub-thumb-meta">
              {hoverChapter && <div className="oden-scrub-thumb-ch">{hoverChapter}</div>}
              <div className="oden-scrub-thumb-time">{formatTime(hoverTime)}</div>
            </div>
          </div>
        </div>
      )}
      <div className="oden-scrub-track">
        {segments.map((s, i) => {
          const w = s.end - s.start;
          const localFill = w > 0 ? Math.min(1, Math.max(0, (progress - s.start) / w)) : 0;
          const localBuf = w > 0 ? Math.min(1, Math.max(0, (bufRatio - s.start) / w)) : 0;
          const isActive = progress >= s.start && progress < s.end;
          // Hover preview: when the user is moving over the bar (or actively
          // scrubbing), render a translucent purple band between the current
          // playhead and the cursor position. Skip when hover is to the same
          // side / position as the playhead (no visible delta).
          let hoverBand: { left: number; width: number } | null = null;
          if (hoverRatio !== null && w > 0) {
            const localHover = Math.min(1, Math.max(0, (hoverRatio - s.start) / w));
            const a = Math.min(localFill, localHover);
            const b = Math.max(localFill, localHover);
            if (b - a > 0.001) hoverBand = { left: a, width: b - a };
          }
          return (
            <div key={i} className="oden-scrub-seg" style={{ flex: w }} data-active={isActive ? '1' : '0'}>
              <div className="oden-scrub-bg" />
              <div className="oden-scrub-buf" style={{ transform: `scaleX(${localBuf})` }} />
              <div className="oden-scrub-fill" style={{ transform: `scaleX(${localFill})` }} />
              {hoverBand && (
                <div
                  className="oden-scrub-hover"
                  style={{ left: `${hoverBand.left * 100}%`, width: `${hoverBand.width * 100}%` }}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className="oden-scrub-handle" style={{ left: `${progress * 100}%` }} />
    </div>
  );
};

export default SeekBar;
